import { Color, Quality } from 'common';
import Data from 'data';
import Locale from 'data/locale';
import { formatRanges, formatStr, gemStats, itemGetColor, itemGetTypes, itemLevelReq, itemsetPartStats, itemsetStats, magicName, modsToStats, runewordStats, statsToLines, statsToLines0, uniqueStats } from './item';
import { isSubType, itemTypeNames } from './itemTypes';
import { hirelingIsClass } from './stateReducers';
import { selectStats } from './stats';

const _L = Locale("ui-item");

const weaponClassMap = {
  'axe': 'WeaponDescAxe',
  'swor': 'WeaponDescSword',
  'knif': 'WeaponDescDagger',
  'spea': 'WeaponDescSpear',
  'jave': 'WeaponDescJavelin',
  'pole': 'WeaponDescPoleArm',
  'club': 'WeaponDescMace',
  'hamm': 'WeaponDescMace',
  'mace': 'WeaponDescMace',
  'scep': 'WeaponDescMace',
  'wand': 'WeaponDescStaff',
  'staf': 'WeaponDescStaff',
  'orb': 'WeaponDescStaff',
  'h2h': 'WeaponDescH2H',
  'bow': 'WeaponDescBow',
  'xbow': 'WeaponDescCrossBow',
  'tpot': 'WeaponDescThrownPotion',
};
function weaponGetClassName(type) {
  if (weaponClassMap[type]) return Locale.strings[weaponClassMap[type]];
  const tinfo = Data.itemTypes[type];
  if (!tinfo) return;
  return (tinfo.equiv1 && weaponGetClassName(tinfo.equiv1)) || (tinfo.equiv2 && weaponGetClassName(tinfo.equiv2));
}

const itemSpeedBreakpoints = {
  // fast/normal/slow/very slow
  //       ama                sor                nec                pal                bar                dru                ass
  'ht1': [[-99,-99,-99,-99], [-99,-99,-99,-99], [-99,-99,-99,-99], [-99,-99,-99,-99], [-99,-99,-99,-99], [-99,-99,-99,-99], [  3, 21, 33, 39]],
  '1hs': [[-10,  7, 18, 34], [-20,  0, 15, 26], [-18,  1, 18, 27], [ -7, 12, 21, 35], [-14,  6, 16, 31], [-18,  5, 14, 24], [ -7, 12, 26, 32]],
  '1ht': [[  1, 14, 24, 38], [-13,  6, 19, 29], [-18,  1, 18, 27], [-21,  0, 11, 26], [-14,  6, 16, 31], [-18,  5, 14, 24], [ -7, 12, 26, 32]],
  '2ht': [[-23, -7,  6, 24], [-40,-16,  1, 13], [-49,-26, -4,  8], [-42,-17, -5, 13], [-35,-12,  1, 18], [-43,-15, -4,  8], [-64,-35,-15, -4]],
  'stf': [[-38,-20,  1, 15], [-12,  7, 24, 34], [-24, -5, 13, 24], [-28,  1,  8, 22], [-35,-12,  1, 18], [ -6, 15, 23, 32], [-35,-12,  5, 14]],
  'bow': [[ -7,  7, 18, 34], [-13,  6, 19, 29], [-12,  6, 22, 31], [-14,  6, 16, 31], [ -7, 12, 21, 35], [  1, 20, 27, 36], [-14,  6, 20, 27]],
  'xbw': [[-53,-33,-17,  5], [-33,-11,  5, 17], [-24, -5, 13, 24], [-42,-17, -5, 13], [-42,-17, -5, 13], [-24,  1,  9, 20], [-49,-23, -5,  5]],
};
function weaponGetSpeedName(charClass, wclass, speed) {
  const cid = Data.classes.indexOf(charClass);
  const bp = itemSpeedBreakpoints[wclass]?.[cid];
  if (!bp) return null;
  if (speed >= bp[3]) return Locale.strings.WeaponAttackVerySlow;
  if (speed >= bp[2]) return Locale.strings.WeaponAttackSlow;
  if (speed >= bp[1]) return Locale.strings.WeaponAttackNormal;
  if (speed >= bp[0]) return Locale.strings.WeaponAttackFast;
  return Locale.strings.WeaponAttackVeryFast;
}

function formatStrValue(str, value) {
  if (Data.info.legacyFormat) return `${str} ${value}`;
  else return formatStr(str, value);
}
function formatStrValuePercent(str, value) {
  if (Data.info.legacyFormat) return `${str} ${value}%`;
  else return formatStr(str, value);
}
function formatStrRange(str, a, b, sep, collapse) {
  if (Data.info.legacyFormat) {
    if (collapse) {
      return `${str} ${a}`;
    } else {
      return `${str} ${a} ${sep} ${b}`;
    }
  } else {
    return formatStr(str, a, b);
  }
}

function runewordTemplate(item, charData={}, type) {
  const uniq = Data.runes[item.unique];
  if (!uniq) return _L`Unknown item`;

  const lines = [];

  const title = Data.runes[item.unique]?.name;
  lines.push(`\\xffc${Color.GOLD}${Locale.strings[title] || title}`);

  const types = [uniq.itype1, uniq.itype2, uniq.itype3, uniq.itype4, uniq.itype5, uniq.itype6]
    .filter(t => t && (!type || (t === 'pala' && type === 'shld') || isSubType(t, type)))
    .map(type => _L(itemTypeNames[type])).join(', ');
  lines.push(`\\xffc${Color.GRAY}${types}`);

  const runes = [uniq.rune1, uniq.rune2, uniq.rune3, uniq.rune4, uniq.rune5, uniq.rune6].filter(Boolean);
  lines.push(`\\xffc${Color.GOLD}'${runes.map(id => {
    const ltr = Data.gems[id]?.letter;
    return Data.info.legacyFormat ? ltr : (Locale.strings[ltr] || ltr);
  }).join('')}'`);

  const level = Math.max(...runes.map(id => Data.items[id].levelreq));
  if (level && level > 1) {
    lines.push(`\\xffc${(charData.level || 99) < level ? Color.RED : Color.WHITE}${formatStrValue(Locale.strings.ItemStats1p, level)}`);
  }

  const stats = {...item.stats};
  stats.item_numsockets = runes.length;
  for (const id in stats) {
    if (stats[id] && typeof stats[id] === 'object') {
      const {min, max} = stats[id];
      if (max < 0) {
        stats[id] = `-${-max}-${-min}`;
      } else {
        stats[id] = `${min}-${max}`;
      }
    }
  }
  const statLines = statsToLines(stats, charData);
  lines.push(...statLines.map(line => `\\xffc${Color.BLUE}${line[1]}`));

  return lines;
}

function templateStats(item, charData) {
  const modmin = new Map(), modmax = new Map();
  let modidx = 0;

  const transformValues = values => values.map(value => {
    if (typeof value === 'object') {
      modmin.set(modidx, value.min);
      modmax.set(modidx, value.max);
      return `{${modidx++}}`;
    } else {
      return value;
    }
  });

  const stats = {};
  if (item.unique) {
    if (item.quality === Quality.UNIQUE) {
      uniqueStats(stats, Data.uniqueItems[item.unique], transformValues(item.uniqueValues), item);
    } else if (item.quality === Quality.SET) {
      uniqueStats(stats, Data.setItems[item.unique], transformValues(item.uniqueValues), item);
    } else if (item.quality === Quality.RUNEWORD) {
      runewordStats(stats, Data.runes[item.unique], transformValues(item.uniqueValues), item);
    }
  }

  const mods = {};
  for (const group of [item.mods, item.auto, item.crafted, item.superior, item.staff].filter(Boolean)) {
    for (const [id, values] of Object.entries(group)) {
      mods[id] = transformValues(values);
    }
  }

  modsToStats(stats, mods, item);

  const statLines = statsToLines0(stats, charData);
  for (const line of statLines) {
    line[1] = formatRanges(line[1], modmin, modmax, true);
  }
  return statLines;
}

export default function itemTooltip(item, state, merc, type) {
  const isEmbed = (state && state.embed);
  let charData = (state && state.profiles[state.activeProfile]) || {};
  if (merc) {
    charData = {
      ...charData,
      class: charData.merc,
      level: charData.mercLevel,
      items: charData.mercItems,
    };
  }

  if (item.quality === Quality.RUNEWORD && item.template) {
    return runewordTemplate(item, charData, type);
  }

  let charStats = state && selectStats(state);
  charStats = charStats && (merc ? charStats.mercenary : charStats.player);

  const { class: charClass = 'sor', level: charLevel = 99 } = charData;

  const lines = [];
  const base = Data.items[item.base];
  if (!base) return _L`Unknown item`;
  const tinfo = Data.itemTypes[base.type];
  const types = itemGetTypes(item.base);

  const color = itemGetColor(item);
  const baseNames = (Locale.strings[base.namestr] || base.name).split('\n').reverse();
  const baseName = baseNames[0];

  const showCharClass = (isEmbed ? tinfo?.class : charClass);
  const charInfo = Data.charStats[showCharClass];

  const prefix = (item.personalized ? `${item.personalized}'s ` : '');

  let title;
  switch (item.quality) {
  case Quality.LOW:
    lines.push(`\\xffc${color}${prefix}${Locale.strings.Crude} ${baseName}`);
    break;
  case Quality.NORMAL:
    lines.push(...baseNames.map((name, i) => `\\xffc${color}${i ? '' : prefix}${name}`));
    break;
  case Quality.SUPERIOR:
    lines.push(`\\xffc${color}${prefix}${Locale.strings.Hiquality} ${baseName}`);
    break;
  case Quality.MAGIC:
    lines.push(`\\xffc${color}${prefix}${magicName(item)}`);
    break;
  case Quality.RARE:
  case Quality.CRAFTED:
    if (item.name) lines.push(`\\xffc${color}${prefix}${item.name}`);
    lines.push(`\\xffc${color}${baseName}`);
    break;
  case Quality.SET:
    if (!item.unidentified) {
      title = Data.setItems[item.unique]?.index;
      title = Locale.strings[title] || title;
      lines.push(`\\xffc${color}${prefix}${title}`);
    }
    lines.push(`\\xffc${color}${baseName}`);
    break;
  case Quality.UNIQUE:
    if (!item.unidentified) {
      title = Data.uniqueItems[item.unique]?.index;
      title = Locale.strings[title] || title;
      lines.push(`\\xffc${color}${prefix}${title}`);
    }
    if (!base.skipname) {
      lines.push(`\\xffc${color}${baseName}`);
    }
    break;
  case Quality.RUNEWORD:
    title = Data.runes[item.unique]?.name;
    title = Locale.strings[title] || title;
    lines.push(`\\xffc${color}${title}`);
    lines.push(`\\xffc${Color.GRAY}${prefix}${baseName}`);
    break;
  // no default
  }

  if (item.socketedItems && item.quality !== Quality.SET) {
    const text = item.socketedItems.map(id => {
      const ltr = Data.gems[id]?.letter;
      return Data.info.legacyFormat ? ltr : (Locale.strings[ltr] || ltr);
    }).filter(Boolean).join('');
    if (text) lines.push(`\\xffc${Color.GOLD}'${text}'`);
  }

  if (item.base === 'box') lines.push(`\\xffc${Color.GOLD}${Locale.strings.RightClicktoOpen}`);
  if (types.has('char')) lines.push(`\\xffc0${Locale.strings.Charmdes}`);
  if (types.has('sock')) lines.push(`\\xffc0${Locale.strings.ExInsertSocketsX}`);

  if (base.spelldesc === 1) lines.push(`\\xffc0${Locale.strings[base.spelldescstr]}`);
  if (base.spelldesc === 2) {
    if (Data.info.legacyFormat) {
      lines.push(`\\xffc0${Locale.strings[base.spelldescstr]} ${base.spelldesccalc}`);
    } else {
      lines.push(`\\xffc0${formatStr(Locale.strings[base.spelldescstr], base.spelldesccalc)}`);
    }
  }

  const displayStats = {...item.stats};
  if (item.socketedItems) {
    for (const id of item.socketedItems) {
      gemStats(displayStats, id, types);
      const sock = state?.items[id];
      if (sock && sock.stats) {
        for (const id in sock.stats) {
          displayStats[id] = (displayStats[id] || 0) + sock.stats[id];
        }
      }
    }
  }
  const stats = {...displayStats};

  let setLocalStats = null, setGlobalStats = null;
  let setItemIds = null, setitemEquipped = null;

  const getmin = value => (value && typeof value === 'object' ? value.min : value || 0);
  const getmax = value => (value && typeof value === 'object' ? value.max : value || 0);
  const addto = (to, from) => (to && typeof to === 'object' ? {min: to.min + from, max: to.max + from} : (to || 0) + from);

  if (!item.unidentified && item.quality === Quality.SET) {
    const uniq = Data.setItems[item.unique];
    const set = uniq && Data.sets[uniq.set];
    if (set) {
      const ids = setItemIds = Object.keys(Data.setItems).filter(id => Data.setItems[id].set === uniq.set).sort().reverse();
      const index = Math.max(0, ids.indexOf(item.unique));
      const equipped = setitemEquipped = ids.map(id => item.template || id === item.unique);
      if (state) {
        for (const oid of Object.values(charData.items)) {
          const oitem = state.items[oid];
          const oindex = ids.indexOf(oitem?.unique);
          if (oindex >= 0) equipped[oindex] = true;
        }
      }
      const count = equipped.reduce((sum, cur) => sum + (cur ? 1 : 0), 0);

      setLocalStats = itemsetPartStats(uniq, equipped.slice(), index, item);
      setGlobalStats = itemsetStats({}, set, count, ids.length);

      for (const entry of setLocalStats) {
        for (const id in entry.stats) {
          stats[id] = addto(stats[id], entry.stats[id]);
        }
      }
    }
  }

  if (Data.gems[item.base]) {
    lines.push('');
    const gemtypes = [[Locale.strings.GemXp3, 'weap'], [Locale.strings.GemXp4, 'tors'], [Locale.strings.GemXp1, 'helm'], [Locale.strings.GemXp2, 'shld']];
    const linesep = types.has('rune') ? ',\n' : ', ';
    for (const [desc, type] of gemtypes) {
      const stats = gemStats({}, item.base, new Set([type]));
      const slines = statsToLines(stats, charData).map(line => line[1]);
      if (!slines.length) continue;
      if (!types.has('rune')) slines.reverse();
      lines.push(...`\\xffc0${desc} ${slines.join(linesep)}`.split('\n'));
    }
    lines.push('');
  }

  if (base.maxac) {
    let def = item.defense || base.maxac;
    const odef = def;
    let enhanced = false;
    if (item.template) {
      let {minac, maxac} = base;
      if (stats.item_armor_percent) {
        if (item.quality !== Quality.RUNEWORD && (item.quality !== Quality.UNIQUE || item.base === Data.uniqueItems[item.unique]?.code)) {
          minac = maxac = maxac + 1;
        }
      }
      if (stats.item_ethereal || item.ethereal) {
        minac += Math.floor(minac * 0.5);
        maxac += Math.floor(maxac * 0.5);
      }
      if (stats.item_armor_percent || stats.item_armorpercent_perlevel) {
        const pct_min = getmin(stats.item_armor_percent) + ((1 * getmin(stats.item_armorpercent_perlevel) / 8) | 0);
        const pct_max = getmax(stats.item_armor_percent) + ((99 * getmax(stats.item_armorpercent_perlevel) / 8) | 0);
        minac += (minac * pct_min / 100) | 0;
        maxac += (maxac * pct_max / 100) | 0;
      }
      if (stats.armorclass) {
        minac += getmin(stats.armorclass);
        maxac += getmax(stats.armorclass);
      }
      if (stats.item_armor_perlevel) {
        minac += (getmin(stats.item_armor_perlevel) * 1 / 8) | 0;
        maxac += (getmax(stats.item_armor_perlevel) * 99 / 8) | 0;
      }
      def = (minac === maxac ? maxac : `${minac}-${maxac}`);
      enhanced = (maxac > odef);
    } else {
      if (stats.item_ethereal || item.ethereal) {
        def += Math.floor(def * 0.5);
        if (item.ethbugged) def += Math.floor(def * 0.5);
      }
      if (item.quality === Quality.LOW) {
        def = Math.floor(def * 0.75);
      }
      if (stats.item_armor_percent || stats.item_armorpercent_perlevel) {
        const pct = (stats.item_armor_percent || 0) + ((charLevel * (stats.item_armorpercent_perlevel || 0) / 8) | 0);
        def += (def * pct / 100) | 0;
      }
      if (stats.armorclass) {
        def += stats.armorclass;
      }
      if (stats.item_armor_perlevel) {
        def += (stats.item_armor_perlevel * charLevel / 8) | 0;
      }
      enhanced = (def > odef);
    }
    lines.push(`\\xffc0${formatStrValue(Locale.strings.ItemStats1h, `${enhanced ? `\\xffc${Color.BLUE}` : ''}${def}`)}`);
  }

  if (base.block != null && types.has('shld')) {
    if (item.template) {
      function blockChance(factor) {
        const min = Math.min(75, base.block + getmin(stats.toblock) + factor);
        const max = Math.min(75, base.block + getmax(stats.toblock) + factor);
        return (min === max ? max : `${min}-${max}`);
      }
      const value = (showCharClass ? blockChance(charInfo?.blockfactor || 0) : `${blockChance(20)}% / ${blockChance(25)}% / ${blockChance(30)}`);
      lines.push(`\\xffc0${formatStrValuePercent(Locale.strings.ItemStats1r, `${stats.toblock ? `\\xffc${Color.BLUE}` : ''}${value}`)}`);
    } else {
      const block = base.block + (stats.toblock || 0) + (charInfo?.blockfactor || 0);
      lines.push(`\\xffc0${formatStrValuePercent(Locale.strings.ItemStats1r, `${stats.toblock ? `\\xffc${Color.BLUE}` : ''}${Math.min(75, block)}`)}`);
    }
  }

  function addDamageLine(label, min, max, noeth, fixmax, nobuff, twovalLabel) {
    let color = '';
    if ((stats.item_ethereal || item.ethereal) && !noeth) {
      min += Math.floor(min * 0.5);
      max += Math.floor(max * 0.5);
    }
    if (item.quality === Quality.LOW) {
      min = Math.max(1, Math.floor(min * 0.75));
      max = Math.max(1, Math.floor(max * 0.75));
    }
    const omin = min, omax = max;
    if (item.template) {
      let minmin = min, minmax = min, maxmin = max, maxmax = max;
      if (!nobuff && (stats.mindamage || stats.maxdamage || stats.item_mindamage_percent || stats.item_maxdamage_percent || stats.item_maxdamage_perlevel || stats.item_maxdamage_percent_perlevel)) {
        const pctlvl_min = (charLevel * getmin(stats.item_maxdamage_percent_perlevel) / 8) | 0;
        const flatlvl_min = (charLevel * getmin(stats.item_maxdamage_perlevel) / 8) | 0;
        const pctlvl_max = (charLevel * getmax(stats.item_maxdamage_percent_perlevel) / 8) | 0;
        const flatlvl_max = (charLevel * getmax(stats.item_maxdamage_perlevel) / 8) | 0;
        minmin += ((minmin * getmin(stats.item_mindamage_percent) / 100) | 0) + getmin(stats.mindamage);
        minmax += ((minmax * getmax(stats.item_mindamage_percent) / 100) | 0) + getmax(stats.mindamage);
        maxmin += ((maxmin * (pctlvl_min + getmin(stats.item_maxdamage_percent)) / 100) | 0) + getmin(stats.maxdamage) + flatlvl_min;
        maxmax += ((maxmax * (pctlvl_max + getmax(stats.item_maxdamage_percent)) / 100) | 0) + getmax(stats.maxdamage) + flatlvl_max;
        color = `\\xffc${Color.BLUE}`;
      }
      if (fixmax && !types.has('miss')) {
        maxmin = Math.max(maxmin, minmin + 1);
        maxmax = Math.max(maxmax, minmin + 1);
      }
      min = (minmin === minmax ? minmax : `(${minmin}-${minmax})`);
      max = (maxmin === maxmax ? maxmax : `(${maxmin}-${maxmax})`);
      if (minmax > omin || maxmax > omax) {
        color = `\\xffc${Color.BLUE}`;
      }
    } else {
      if (!nobuff && (stats.mindamage || stats.maxdamage || stats.item_mindamage_percent || stats.item_maxdamage_percent || stats.item_maxdamage_perlevel || stats.item_maxdamage_percent_perlevel)) {
        const pctlvl = (charLevel * (stats.item_maxdamage_percent_perlevel || 0) / 8) | 0;
        const flatlvl = (charLevel * (stats.item_maxdamage_perlevel || 0) / 8) | 0;
        min += ((min * (stats.item_mindamage_percent || 0) / 100) | 0) + (stats.mindamage || 0);
        max += ((max * (pctlvl + (stats.item_maxdamage_percent || 0)) / 100) | 0) + (stats.maxdamage || 0) + flatlvl;
      }
      if (nobuff) {
        min += charStats?.base.smite_mindamage || 0;
        max += charStats?.base.smite_maxdamage || 0;
      }
      if (fixmax && !types.has('miss')) max = Math.max(max, min + 1);
      if (!nobuff && (min > omin || max > omax)) {
        color = `\\xffc${Color.BLUE}`;
      }
    }
    if (!Data.info.legacyFormat && min !== max && twovalLabel) {
      label = twovalLabel;
    }
    lines.push(`\\xffc0${formatStrRange(label, `${color}${min}`, max, Locale.strings.ItemStast1k)}`);
  }
  if (types.has('tpot')) {
    const miss = Data.missiles[base.missiletype];
    if (miss) {
      let min = (miss.mindamage || 0) + (miss.emin || 0);
      let max = (miss.maxdamage || 0) + (miss.emax || 0);
      let color = '';
      switch (miss.etype) {
      case 'fire': color = `\\xffc${Color.RED}`; break;
      case 'pois': color = `\\xffc${Color.GREEN}`; break;
      case 'cold': color = `\\xffc${Color.BLUE}`; break;
      // no default
      }
      if (miss.etype === 'pois') {
        min = Math.round(min * 25 / miss.elen);
        max = Math.round(max * 25 / miss.elen);
      }
      lines.push(`\\xffc0${formatStrRange(Locale.strings.ItemStats1n, `${color}${min}`, max, Locale.strings.ItemStast1k, max <= min)}`);
    }
  } else if (types.has('weap')) {
    if (types.has('thro')) {
      addDamageLine(Locale.strings.ItemStats1n, base.minmisdam, base.maxmisdam, false, false, false, Locale.strings.strItemStatThrowDamageRange);
    }
    if (!base['2handed'] || (base['1or2handed'] && (showCharClass === 'bar' || !showCharClass))) {
      addDamageLine(Locale.strings.ItemStats1l, base.mindam, base.maxdam, false, true);
    }
    if (base['2handed']) {
      addDamageLine(Locale.strings.ItemStats1m, base['2handmindam'], base['2handmaxdam'], false, true);
    }
  }
  if (types.has('shld') && (showCharClass === 'pal' || !showCharClass)) {
    addDamageLine(Locale.strings.ItemStats1o, base.mindam, base.maxdam, true, false, true);
  }
  if (types.has('boot') && (showCharClass === 'ass' || !showCharClass)) {
    addDamageLine(Locale.strings.ModStre10k, base.mindam, base.maxdam, true, false, true);
  }

  let attackSpeedLine = null;
  if (base.wclass) {
    let speed = base.speed || 0;
    let color = '';
    if (stats.item_fasterattackrate) {
      speed -= getmax(stats.item_fasterattackrate);
      color = `\\xffc${Color.BLUE}`;
    }
    const strClass = weaponGetClassName(base.type);
    const strSpeed = weaponGetSpeedName(showCharClass || charClass, base.wclass, speed);
    if (strClass && strSpeed) {
      attackSpeedLine = Data.info.legacyFormat ? `\\xffc0${strClass} - ${color}${strSpeed}` : `\\xffc0${formatStr(strClass, `${color}${strSpeed}`)}`;
      if (item.quality === Quality.SET && !item.unidentified) {
        // for some reason on set items attack speed is before requirements instead of after
        lines.push(attackSpeedLine);
        attackSpeedLine = null;
      }
    }
  }

  if (Data.belts && Locale.strings.BeltStorageModifierInfo && types.has('belt')) {
    const slots = Data.belts[base.belt] - 4;
    if (slots) lines.push(formatStr(Locale.strings.BeltStorageModifierInfo, slots));
  }

  if (!stats.item_indesctructible && base.durability && !base.nodurability && (!base.stackable || types.has('thro'))) {
    let value = base.durability;
    if (stats.item_ethereal || item.ethereal) {
      value = (value >> 1) + 1;
    }
    if (item.quality === Quality.LOW) {
      value = Math.floor((value - 1) / 3);
    }
    if (stats.item_maxdurability_percent) {
      value += (value * getmax(stats.item_maxdurability_percent) / 100) | 0;
    }
    value += getmax(stats.maxdurability);
    lines.push(`\\xffc0${formatStrRange(Locale.strings.ItemStats1d, value, value, Locale.strings.ItemStats1j)}`);
  }

  if (base.stackable && !item.unidentified) {
    let value = base.maxstack;
    if (stats.item_extra_stack) {
      value += getmax(stats.item_extra_stack);
    }
    lines.push(`\\xffc0${formatStrRange(Locale.strings.ItemStats1i, value, value, Locale.strings.ItemStats1j)}`);
  }

  if (tinfo.class) {
    let color = tinfo.class === charClass || isEmbed ? Color.WHITE : Color.RED;
    if (merc && hirelingIsClass(Data.hireling[charClass]?.[0]?.class, tinfo.class)) color = Color.WHITE;
    lines.push(`\\xffc${color}${Locale.istrings[Data.strClassOnly[Data.classes.indexOf(tinfo.class)]]}`);
  }

  if (base.reqdex) {
    let value = base.reqdex;
    if (stats.item_req_percent) {
      value += (value * getmax(stats.item_req_percent) / 100) | 0;
    }
    if (stats.item_ethereal || item.ethereal) {
      value -= 10;
    }
    //TODO: stats
    const color = (charStats && charStats.base.dexterity < value && !isEmbed ? Color.RED : Color.WHITE);
    lines.push(`\\xffc${color}${formatStrValue(Locale.strings.ItemStats1f, value)}`);
  }

  if (base.reqstr) {
    let value = base.reqstr;
    if (stats.item_req_percent) {
      value += (value * getmax(stats.item_req_percent) / 100) | 0;
    }
    if (stats.item_ethereal || item.ethereal) {
      value -= 10;
    }
    //TODO: stats
    const color = (charStats && charStats.base.strength < value && !isEmbed ? Color.RED : Color.WHITE);
    lines.push(`\\xffc${color}${formatStrValue(Locale.strings.ItemStats1e, value)}`);
  }

  if (!item.unidentified) {
    const level = itemLevelReq(item, state, merc);
    if (level && level > 1) {
      lines.push(`\\xffc${charLevel < level && !isEmbed ? Color.RED : Color.WHITE}${formatStrValue(Locale.strings.ItemStats1p, level)}`);
    }
  }

  if (attackSpeedLine) {
    lines.push(attackSpeedLine);
  }

  if (displayStats) {
    // for some weird reason, undead damage bonus is at the END of the tooltip unless the stat is already present on the item
    let maceEnd = false;
    if (types.has('blun') && displayStats.item_undeaddamage_percent === 50) {
      delete displayStats.item_undeaddamage_percent;
      maceEnd = true;
    }

    const corruptedStats = new Set();
    if (item.corrupted) {
      const stats = modsToStats({}, item.corrupted, item);
      delete stats.corrupted;
      for (const line of statsToLines0(stats, charData)) {
        corruptedStats.add(line[0]);
      }
    }

    let statLines;
    if (item.template) {
      statLines = templateStats(item, charData);
    } else {
      statLines = statsToLines0(displayStats, charData);
    }

    statLines = statLines.map(line => `\\xffc${Color.BLUE}${line[1]}${corruptedStats.has(line[0]) ? '\\xffc1*' : ''}`);
    lines.push(...statLines);
    if (maceEnd) {
      if (Data.info.legacyFormat) {
        lines.push(`\\xffc${Color.BLUE}+50% ${Locale.strings.ModStr4f}`);
      } else {
        lines.push(`\\xffc${Color.BLUE}${formatStr(Locale.strings.ModStr4f, 50)}`);
      }
    }
  } else if (item.unidentified) {
    lines.push(`\\xffc${Color.RED}${Locale.strings.ItemStats1b}`);
  }

  if (stats.item_ethereal || item.ethereal || item.sockets) {
    const mods = [];
    if (stats.item_ethereal || item.ethereal) {
      mods.push(Locale.istrings[22745]);
    }
    const sockets = (item.template ? displayStats.item_numsockets : item.sockets);
    if (sockets) {
      const limit = Math.min(base.gemsockets, tinfo.maxsock40, base.invwidth * base.invheight);
      if (typeof sockets === 'object') {
        const min = Math.min(sockets.min, limit);
        const max = Math.min(sockets.max, limit);
        const str = (max > min ? `${min}-${max}` : min);
        if (Data.info.legacyFormat) {
          mods.push(`${Locale.strings.Socketable} (${str})`);
        } else {
          mods.push(formatStr(Locale.strings.Socketable, str));
        }
      } else if (Data.info.legacyFormat) {
        mods.push(`${Locale.strings.Socketable} (${Math.min(sockets, limit)})`);
      } else {
        mods.push(formatStr(Locale.strings.Socketable, Math.min(sockets, limit)));
      }
    }
    lines.push(`\\xffc${Color.BLUE}` + mods.join(", "));
  }

  if (item.quality === Quality.SET && !item.unidentified) {
    const uniq = Data.setItems[item.unique];
    const set = uniq && Data.sets[uniq.set];
    if (set) {
      for (const entry of setLocalStats) {
        const partLines = statsToLines(entry.stats, charData);
        let suffix = '';
        if (item.template) {
          if (uniq.addfunc === 1) {
            let index = setItemIds.length - entry.index - 1;
            if (setItemIds.indexOf(item.unique) <= index) index += 1;
            const other = Data.setItems[setItemIds[index]];
            suffix = ` \\xffc0(${Locale.strings[other.index] || other.index})`;
          } else if (uniq.addfunc === 2) {
            suffix = ` \\xffc0(${entry.index + 1} Items)`;
          }
        }
        lines.push(...partLines.map(line => `\\xffc${Color.GREEN}${line[1]}${suffix}`));
      }

      const setLines = statsToLines(setGlobalStats, charData);
      if (setLines.length) {
        lines.push('', ...setLines.map(line => `\\xffc${Color.GOLD}${line[1]}`));
      }

      lines.push('');
      lines.push(`\\xffc${Color.GOLD}${Locale.strings[set.name] || set.name}`);
      const ids = Object.keys(Data.setItems).filter(id => Data.setItems[id].set === uniq.set).sort().reverse();
      for (let i = 0; i < ids.length; ++i) {
        const id = ids[i];
        const equipped = setitemEquipped[i];
        const color = equipped ? Color.GREEN : Color.RED;
        const curitem = Data.setItems[id];
        lines.push(`\\xffc${color}${Locale.strings[curitem.index] || curitem.index}`);
      }
    }
  }

  return lines;
}
